import React, { useEffect, useMemo, useRef, forwardRef, useImperativeHandle, Ref } from 'react';
import { path } from 'ramda';
import styled from 'styled-components';
import { FormikContextType } from 'formik';
import { Row, Col, Checkbox, Input as AntdInput } from 'antd';

import {
  SendCommunicationSecondStepForm,
  MessageMask,
  CommunicationContent,
  SendCommunicationCustomersSelection,
  SendCommunicationChannels,
  CompensationSentence,
} from 'types';
import { useIntl } from 'common/config/i18n';
import { useTheme, getTheme } from 'common/styling/theme';
import Input from 'components/organisms/input';
import FormError from 'components/molecules/form-error';
import { Editor } from 'components/templates/react-quill';
import { getActiveCompensationByDelay, getHasCompensation } from 'common/utils/send-communication';
import Button from 'components/molecules/button';
import { calculateCharacterCount } from 'common/utils/sms-esendex';

const { TextArea } = AntdInput;

type Props = {
  compensationSentencesData: CompensationSentence[];
  selectedMessageMask?: MessageMask;
  formik: FormikContextType<SendCommunicationSecondStepForm>;
  content: CommunicationContent;
  contentIndex: number;
  defaultPrincipalZoneMessages?: {
    principalZoneLongMessageSaved?: string;
    principalZoneShortMessageSaved?: string;
  };
  customersSelection: SendCommunicationCustomersSelection;
  hasMultipleStations?: boolean;
  handleSpreadTextToAllStations: (
    principalLongMessage?: string,
    principalShortMessage?: string,
    secondaryLongMessage?: string,
    secondaryShortMessage?: string
  ) => void;
  handleSpreadDelayToAllDelays: (delay?: string) => void;
  onFocusTextField: (contentIndex: number, isPrimary: boolean, isSecondary: boolean, isTertiary: boolean) => void;
  primaryEditorWrapperRef?: Ref<HTMLDivElement>;
  secondaryEditorWrapperRef?: Ref<HTMLDivElement>;
  tertiaryEditorWrapperRef?: Ref<HTMLDivElement>;
};

const SubTitle = styled.div`
  font-size: ${path(['theme', 'font', 'size', 'body'])};
  font-weight: ${path(['theme', 'font', 'weight', 'medium'])};
`;

export const StyledLink = styled.a`
  display: flex;
  align-items: center;
  text-decoration: underline;
  font-weight: bold;
  margin-top: 10px;
  cursor: pointer;
  color: ${getTheme().colors.common.black};
`;
const ButtonWrapper = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: flex-end;
  width: 100%;
  margin-top: 1rem;
`;
const CopyDelayButtonWrapper = styled(Col)`
  margin-left: 1rem;
`;

const NumberOfCharsInShortMsg = ({ message }: { message?: string }) => {
  const { t } = useIntl();
  const { theme } = useTheme();

  return (
    <SubTitle style={{ fontSize: theme.font.size.caption, fontStyle: 'italic', fontWeight: 'normal' }}>
      {t('sendCommunication', 'substitutionSchedule', 'numberOfChars', {
        /*
         * We use trim() to prevent unecessary spaces and then get the right count
         * Because we also trim() the corresponding param of the request
         * See param area2Short in both prepareFetchStationsTemplatesPreviewParams AND prepareSaveCommunicationsParams
         */
        numberOfChars: calculateCharacterCount(message?.trim() || ''),
      })}
    </SubTitle>
  );
};

const ImpactedStation = forwardRef(
  (
    {
      compensationSentencesData,
      selectedMessageMask,
      formik,
      content,
      contentIndex,
      defaultPrincipalZoneMessages,
      hasMultipleStations,
      handleSpreadTextToAllStations,
      handleSpreadDelayToAllDelays,
      customersSelection,
      onFocusTextField,
      primaryEditorWrapperRef,
      secondaryEditorWrapperRef,
      tertiaryEditorWrapperRef,
    }: Props,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ref: any
  ) => {
    const { t } = useIntl();
    const { theme } = useTheme();

    const primaryEditorRef = useRef<HTMLFormElement>(null);
    const secondaryEditorRef = useRef<HTMLFormElement>(null);
    const tertiaryEditorRef = useRef<HTMLFormElement>(null);

    const hasCompensationZone = getHasCompensation(customersSelection, selectedMessageMask);

    const compensationByDelay = useMemo(
      () =>
        compensationSentencesData.find(
          (item) =>
            item.name ===
            getActiveCompensationByDelay({
              compensations: compensationSentencesData,
              delayValue: content.delay,
              hasVolumeDelay: selectedMessageMask?.hasVolumeDelay,
              hasCompensationZone,
            })
        ),
      [compensationSentencesData, selectedMessageMask?.hasVolumeDelay, content.delay, hasCompensationZone]
    );

    useEffect(() => {
      formik.setFieldValue(`communicationContent[${contentIndex}].compensationId`, compensationByDelay?.id);
      formik.setFieldValue(
        `communicationContent[${contentIndex}].compensationLongMessage`,
        compensationByDelay?.longMessage
      );
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [compensationByDelay]);

    useEffect(() => {
      if (defaultPrincipalZoneMessages?.principalZoneLongMessageSaved) {
        formik.setFieldValue(
          `communicationContent[${contentIndex}].principalZoneLongMessage`,
          defaultPrincipalZoneMessages.principalZoneLongMessageSaved
        );
      } else {
        formik.setFieldValue(`communicationContent[${contentIndex}].principalZoneLongMessage`, '');
      }
      if (defaultPrincipalZoneMessages?.principalZoneShortMessageSaved) {
        formik.setFieldValue(
          `communicationContent[${contentIndex}].principalZoneShortMessage`,
          defaultPrincipalZoneMessages.principalZoneShortMessageSaved
        );
      } else {
        formik.setFieldValue(`communicationContent[${contentIndex}].principalZoneShortMessage`, '');
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [defaultPrincipalZoneMessages]);

    useImperativeHandle(ref, () => ({
      insertText(text: string, isPrimary: boolean, isSecondary: boolean, isTertiary: boolean) {
        if (isPrimary) {
          primaryEditorRef?.current?.insertText(text);
        } else if (isSecondary) {
          secondaryEditorRef?.current?.insertText(text);
        } else if (isTertiary) {
          tertiaryEditorRef?.current?.insertText(text);
        }
      },
    }));

    return (
      <>
        {selectedMessageMask?.hasVolumeDelay && (
          <Col style={{ marginTop: 15 }}>
            <Row align="middle">
              <Col span={2}>
                <SubTitle>{t('list_circulation', 'CHART_COM_DELAY')}</SubTitle>
              </Col>
              <Col>
                <Input
                  type="time"
                  name={`communicationContent[${contentIndex}].delay`}
                  height="auto"
                  value={content.delay}
                  onChange={formik.handleChange}
                />
              </Col>
              {hasMultipleStations && contentIndex === 0 && (
                <CopyDelayButtonWrapper>
                  <Button
                    height="35px"
                    width="auto"
                    borderRadius="5px"
                    backgroundColor={path(['colors', 'grey', 'dark'], theme)}
                    color={path(['colors', 'common', 'black'], theme)}
                    fontSize="12px"
                    onClick={() => handleSpreadDelayToAllDelays(content.delay)}
                  >
                    {t('Buttons', 'COPY_DELAY_BUTTON')}
                  </Button>
                </CopyDelayButtonWrapper>
              )}
            </Row>
            <FormError formik={formik} fieldName={`communicationContent[${contentIndex}].delay`} />
          </Col>
        )}
        {selectedMessageMask?.hasSubstituteStation && (
          <Col span={16} style={{ marginTop: 15 }}>
            <Row gutter={16}>
              <Col>
                <SubTitle>{t('sendCommunication', 'substitutionSchedule', 'substitutionLabel')}</SubTitle>
                <Input
                  name={`communicationContent[${contentIndex}].stationReplacement`}
                  placeholder={t('sendCommunication', 'substitutionSchedule', 'placeholder')}
                  value={content.stationReplacement}
                  onChange={formik.handleChange}
                  width="300px"
                  maxLength={50}
                />
                <FormError formik={formik} fieldName={`communicationContent[${contentIndex}].stationReplacement`} />
              </Col>
              <Col>
                <SubTitle>{t('sendCommunication', 'substitutionSchedule', 'scheduleLabl')}</SubTitle>
                <Input
                  type="time"
                  name={`communicationContent[${contentIndex}].newSchedule`}
                  height="auto"
                  value={content.newSchedule}
                  onChange={formik.handleChange}
                />
                <FormError formik={formik} fieldName={`communicationContent[${contentIndex}].newSchedule`} />
              </Col>
            </Row>
          </Col>
        )}
        {selectedMessageMask?.hasDock && (
          <Col style={{ marginTop: 15 }}>
            <Row align="middle">
              <Col span={2}>
                <SubTitle>{t('form', 'PLATFORM')}</SubTitle>
              </Col>
              <Col span={22}>
                <Input
                  onChange={formik.handleChange}
                  value={content.dock}
                  name={`communicationContent[${contentIndex}].dock`}
                  width="40%"
                  maxLength={10}
                />
              </Col>
            </Row>
            <FormError formik={formik} fieldName={`communicationContent[${contentIndex}].dock`} />
          </Col>
        )}
        <Row align="top" gutter={24} style={{ marginTop: 15 }}>
          {selectedMessageMask?.hasPrincipalZoneLongMessage && (
            <Col span={14} ref={primaryEditorWrapperRef}>
              <SubTitle>{t('table', 'COLUMN_INFO')}</SubTitle>
              <Editor
                toolbarId={`communicationContent${contentIndex}principalZoneLongMessage`}
                value={content.principalZoneLongMessage}
                onChange={({ semanticHtml, textWithoutHtml }) => {
                  if (textWithoutHtml && semanticHtml) {
                    if (textWithoutHtml.trim().length === 0) {
                      formik.setFieldValue(`communicationContent[${contentIndex}].principalZoneLongMessage`, '');
                    } else {
                      formik.setFieldValue(
                        `communicationContent[${contentIndex}].principalZoneLongMessage`,
                        semanticHtml
                      );
                    }
                  }
                }}
                onFocus={() => {
                  onFocusTextField(contentIndex, true, false, false);
                }}
                ref={primaryEditorRef}
              />
            </Col>
          )}
          {selectedMessageMask?.hasPrincipalZoneShortMessage &&
            formik.values.communicationChannels?.includes(SendCommunicationChannels.Sms) && (
              <Col span={10}>
                <Row style={{ justifyContent: 'space-between' }}>
                  <SubTitle style={{ fontStyle: 'italic', fontWeight: 'normal' }}>
                    {t('form', 'SHORT_MESSAGE')}
                  </SubTitle>
                  <NumberOfCharsInShortMsg message={content.principalZoneShortMessage} />
                </Row>
                <TextArea
                  autoSize={{ minRows: 5 }}
                  onChange={formik.handleChange}
                  value={content.principalZoneShortMessage}
                  key={defaultPrincipalZoneMessages?.principalZoneShortMessageSaved}
                  name={`communicationContent[${contentIndex}].principalZoneShortMessage`}
                />
              </Col>
            )}
        </Row>
        <Row align="top" gutter={24} style={{ marginTop: 15 }}>
          {selectedMessageMask?.hasSecondaryZoneLongMessage && (
            <Col span={14} ref={secondaryEditorWrapperRef}>
              <SubTitle>{t('table', 'COLUMN_INFO_COMP')}</SubTitle>
              <Editor
                toolbarId={`communicationContent${contentIndex}secondaryZoneLongMessage`}
                value={content.secondaryZoneLongMessage}
                onChange={({ semanticHtml, textWithoutHtml }) => {
                  if (semanticHtml && textWithoutHtml) {
                    if (textWithoutHtml.trim().length === 0) {
                      formik.setFieldValue(`communicationContent[${contentIndex}].secondaryZoneLongMessage`, '');
                    } else {
                      formik.setFieldValue(
                        `communicationContent[${contentIndex}].secondaryZoneLongMessage`,
                        semanticHtml
                      );
                    }
                  }
                }}
                onFocus={() => {
                  onFocusTextField(contentIndex, false, true, false);
                }}
                ref={secondaryEditorRef}
              />
            </Col>
          )}
          {selectedMessageMask?.hasSecondaryZoneShortMessage &&
            formik.values.communicationChannels?.includes(SendCommunicationChannels.Sms) && (
              <Col span={10}>
                <Row style={{ justifyContent: 'space-between' }}>
                  <SubTitle style={{ fontStyle: 'italic', fontWeight: 'normal' }}>
                    {t('form', 'SHORT_MESSAGE')}
                  </SubTitle>
                  <NumberOfCharsInShortMsg message={content.secondaryZoneShortMessage} />
                </Row>
                <TextArea
                  autoSize={{ minRows: 5 }}
                  onChange={formik.handleChange}
                  value={content.secondaryZoneShortMessage}
                  name={`communicationContent[${contentIndex}].secondaryZoneShortMessage`}
                />
              </Col>
            )}
        </Row>
        {hasMultipleStations && contentIndex === 0 && (
          <ButtonWrapper>
            <Button
              height="35px"
              width="auto"
              borderRadius="5px"
              backgroundColor={path(['colors', 'grey', 'dark'], theme)}
              color={path(['colors', 'common', 'black'], theme)}
              fontSize="12px"
              onClick={() => {
                handleSpreadTextToAllStations(
                  content.principalZoneLongMessage,
                  content.principalZoneShortMessage,
                  content.secondaryZoneLongMessage,
                  content.secondaryZoneShortMessage
                );
              }}
            >
              {t('Buttons', 'COPY_INFO_BUTTON')}
            </Button>
          </ButtonWrapper>
        )}
        {hasCompensationZone && (
          <Col style={{ marginTop: 20 }}>
            <Row gutter={16}>
              <Col span={14} ref={tertiaryEditorWrapperRef}>
                <SubTitle>{t('sendCommunication', 'compensation', 'savLabel')}</SubTitle>
                <Editor
                  toolbarId={`communicationContent${contentIndex}compensationLongMessage`}
                  value={content.compensationLongMessage || ''}
                  onChange={({ semanticHtml, textWithoutHtml }) => {
                    if (semanticHtml && textWithoutHtml) {
                      if (textWithoutHtml.trim().length === 0) {
                        formik.setFieldValue(`communicationContent[${contentIndex}].compensationLongMessage`, '');
                      } else {
                        formik.setFieldValue(
                          `communicationContent[${contentIndex}].compensationLongMessage`,
                          semanticHtml
                        );
                      }
                    }
                  }}
                  onFocus={() => {
                    onFocusTextField(contentIndex, false, false, true);
                  }}
                  ref={tertiaryEditorRef}
                />
                <FormError
                  formik={formik}
                  fieldName={`communicationContent[${contentIndex}].compensationLongMessage`}
                />
              </Col>
              <Col span={10}>
                <SubTitle>{t('sendCommunication', 'compensation', 'compensationLabel')}</SubTitle>
                <Col
                  style={{
                    height: 127,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                  }}
                >
                  <Row align="middle">
                    {compensationSentencesData?.map((item) => (
                      <Checkbox
                        key={item.id}
                        checked={content.compensationId === item.id}
                        onChange={() => {
                          formik.setFieldValue(`communicationContent[${contentIndex}].compensationId`, item.id);
                          formik.setFieldValue(
                            `communicationContent[${contentIndex}].compensationLongMessage`,
                            item.longMessage
                          );
                        }}
                      >
                        {item.name}
                      </Checkbox>
                    ))}
                  </Row>
                  <StyledLink
                    onClick={() => {
                      formik.setFieldValue(`communicationContent[${contentIndex}].compensationId`, undefined);
                      formik.setFieldValue(`communicationContent[${contentIndex}].compensationLongMessage`, '');
                      formik.setFieldValue(`communicationContent[${contentIndex}].compensationSelected`, false);
                    }}
                  >
                    {t('sendCommunication', 'compensation', 'cancelCompensation')}
                  </StyledLink>
                </Col>
                <FormError formik={formik} fieldName={`communicationContent[${contentIndex}].compensationId`} />
              </Col>
            </Row>
          </Col>
        )}
      </>
    );
  }
);

ImpactedStation.displayName = 'ImpactedStation';

export default ImpactedStation;
